/**
 * The low-arc firing solution (67.10). Pure.
 *
 * A person-scale shot drops hard: at human scale a rifle round fired level falls a body height
 * ten body lengths out (felt gravity, ruling 15a), so a gun that fires along the line of sight
 * lands far below what the shooter looks at. The bots already lift their aim for the drop
 * (ai/actionBot.ts); this is the same correction for anyone holding a gun, as a closed form
 * under the gravity at the chord's midpoint (constant over a shot's few hundredths of a second).
 *
 *   tan θ = (v² − √(v⁴ − g (g x² + 2 y v²))) / (g x)
 *
 * for a target `x` out along the horizon and `y` up it, at muzzle speed `v` under gravity `g`:
 * the lower of the two arcs (the flat shot; the lob is the mortar's). No real root = out of reach.
 */

import type { Vec3, PlanetState } from '../types';
import { vecAdd, vecScale, vecSub, vecDot, vecNormalize, vecLength } from '../math';
import { dsin, dcos, datan2 } from './dmath';
import { friendlyShare } from './damage';
import type { PlayerState } from '../types';
import { gravityAccel } from './physics';
import { dragAimSpeed, dragKeep } from './ballistics';
import { PHYS_DT } from '../constants';

/**
 * The launch direction (unit vector) that lands a shot from `muzzle` on `target` at `speed`
 * under the gravity vector `g`, or null when the target is out of reach at that speed.
 * With no gravity, the straight line.
 */
export function solveLowArc(muzzle: Vec3, target: Vec3, speed: number, g: Vec3): Vec3 | null {
  const d = vecSub(target, muzzle);
  const gm = vecLength(g);
  if (gm < 1e-12) return vecLength(d) > 0 ? vecNormalize(d) : null;
  const up = vecScale(g, -1 / gm);
  const y = vecDot(d, up);
  const horiz = vecSub(d, vecScale(up, y));
  const x = vecLength(horiz);
  const v2 = speed * speed;
  if (x < 1e-9) {
    // straight up or down: reachable up only as far as the shot climbs
    if (y <= 0) return vecScale(up, -1);
    return v2 >= 2 * gm * y ? up : null;
  }
  const disc = v2 * v2 - gm * (gm * x * x + 2 * y * v2);
  if (disc < 0) return null;
  const theta = datan2(v2 - Math.sqrt(disc), gm * x);
  return vecNormalize(vecAdd(vecScale(horiz, dcos(theta) / x), vecScale(up, dsin(theta))));
}

/**
 * The aim pitch (radians above the surface's horizon, as `aimDirection` takes it) that lands a shot
 * from `muzzle` on `target`, measured against the shooter's up `n`; null when out of reach.
 * Only the elevation is returned: the shooter's heading supplies the bearing.
 */
export function solveAimPitch(muzzle: Vec3, target: Vec3, n: Vec3, speed: number, g: Vec3): number | null {
  const dir = solveLowArc(muzzle, target, speed, g);
  if (!dir) return null;
  const s = Math.max(-1, Math.min(1, vecDot(dir, n)));
  return datan2(s, Math.sqrt(Math.max(0, 1 - s * s)));
}

/**
 * 65.30: where to aim so a round at `speed` under `drag` and the fall `g` lands on `at` — a point along the low arc's launch
 * direction (at the drag's speed, dragAimSpeed), as far out as `at`; the bots' lift. The flat drop (`at` less g · the
 * fall's t², ballistics.ts fallTime2) under-lifted a lofted shot (the 9 mm by 7 people at 90 people, the 7.62 by 1.6 at 150
 * before the drag); the arc lands within a tenth of a person to the round's effective range. Null out of its reach.
 */
export function arcAimPoint(muzzle: Vec3, at: Vec3, speed: number, drag: number, g: Vec3): Vec3 | null {
  const d = vecLength(vecSub(at, muzzle));
  const dir = solveLowArc(muzzle, at, dragAimSpeed(speed, drag, d), g);
  return dir ? vecAdd(muzzle, vecScale(dir, d)) : null;
}

/**
 * 65.30: the aim pitch for a round under drag (core/ballistics.ts). The closed form has none, so it is given the speed whose
 * no-drag fall over the distance is the dragged round's (dragAimSpeed); then, with `fly` (the round's flight, as predictShot
 * takes it), one fixed-point pass — the shot flown to the target's range, the aim point lifted by its miss along `n`, solved
 * again. With no drag, solveAimPitch itself.
 */
export function solveAimPitchDrag(muzzle: Vec3, target: Vec3, n: Vec3, speed: number, drag: number, g: Vec3, fly?: { life: number; fall: number; gravityScale: number; planets: readonly PlanetState[]; stepLength: number; lob?: boolean }): number | null {
  // (65.31: a lob — the 40 mm's slow round over long ranges — has no drag but the closed form's one gravity misses on the
  // planet's curve: `fly.lob` runs the same fixed-point passes, three, without the drag; bullets and the rocket as before)
  const lob = !(drag > 0) && fly?.lob === true;
  if (!(drag > 0) && !lob) return solveAimPitch(muzzle, target, n, speed, g);
  const v = lob ? speed : dragAimSpeed(speed, drag, vecLength(vecSub(target, muzzle)));
  let pitch = solveAimPitch(muzzle, target, n, v, g);
  if (pitch === null || !fly) return pitch;
  const d = vecSub(target, muzzle), flat = vecSub(d, vecScale(n, vecDot(d, n))), range = vecLength(flat);
  if (!(range > 0)) return pitch;
  const fwd = vecScale(flat, 1 / range);
  let lift = 0;
  for (let pass = 0; pass < (lob ? 3 : 1); pass++) {
    const dir = vecAdd(vecScale(fwd, dcos(pitch!)), vecScale(n, dsin(pitch!)));
    const shot = predictShot(muzzle, vecScale(dir, speed), { ...fly, drag, blocked: p => vecDot(vecSub(p, muzzle), fwd) >= range });
    if (!shot.hit) return pitch;
    lift += vecDot(vecSub(target, shot.at), n);
    const next = solveAimPitch(muzzle, vecAdd(target, vecScale(n, lift)), n, v, g);
    if (next === null) return pitch;
    pitch = next;
  }
  return pitch;
}

/**
 * The fall a round feels, as a multiple of the felt gravity: its weapon's own (a rocket flies on its motor: 0), else a
 * bullet's. The sim's flight reads the same (core/bullets.ts); an aim or a landing preview that assumed a bullet's
 * drop for a rocket lifted it over the target (the owner, Oct 10: the rocket's "resolved drop point" was off).
 */
export function shotFall(spec: { gravity?: number }, bulletGravityScale: number): number {
  return spec.gravity ?? bulletGravityScale;
}

/**
 * Where a shot fired from `muzzle` at `vel` stops: the first point `blocked` says it meets, or where its `life` runs
 * out (a rocket bursts there) — flown in steps of `stepLength` under the planets' gravity × `fall` × `gravityScale`,
 * as the sim flies it (62.52: and `wind`, the wind's pull, so the dot drifts as the round will). The wind is one pull (the
 * muzzle's), or a field (65.25e): asked where the round is, `t` seconds into its flight, every `windStep` seconds of it
 * (a sim tick by default, as core/bullets.ts takes it) — since the wind downrange is not the muzzle's (a third less drift at 90 people in a storm).
 * The preview of what the crosshair's shot will do. Pure (the field is the caller's).
 */
export function predictShot(muzzle: Vec3, vel: Vec3, o: { drag?: number; life: number; fall: number; gravityScale: number; planets: readonly PlanetState[]; blocked: (p: Vec3) => boolean; stepLength: number; maxSteps?: number; wind?: Vec3 | null | ((p: Vec3, t: number) => Vec3 | null); windStep?: number }): { at: Vec3; hit: boolean; t: number } {
  let pos = muzzle, v = vel, t = 0;
  const speed = vecLength(vel);
  if (speed < 1e-12) return { at: muzzle, hit: false, t: 0 };
  const h = o.stepLength / speed, n = Math.min(o.maxSteps ?? 2000, Math.ceil(o.life / h));
  const field = typeof o.wind === 'function' ? o.wind : null;
  let wind: Vec3 | null = field ? null : (o.wind as Vec3 | null | undefined) ?? null, ask = 0;
  for (let i = 0; i < n; i++) {
    if (field && t >= ask) { wind = field(pos, t); ask += o.windStep ?? PHYS_DT; }   // (the field: once a step of flight, where the round is)
    if (o.drag) v = vecScale(v, dragKeep(vecLength(v), o.drag, h));   // 65.30: its round's drag first, as the sim flies it
    if (o.fall !== 0) v = vecAdd(v, vecScale(gravityAccel(pos, o.planets as PlanetState[]), o.fall * o.gravityScale * h));
    if (wind) v = vecAdd(v, vecScale(wind, h));   // 62.52: the wind's pull (core/wind.ts actionWindAccel), as the sim flies it
    const next = vecAdd(pos, vecScale(v, h));
    t += h;
    if (o.blocked(next)) return { at: next, hit: true, t };
    pos = next;
  }
  return { at: pos, hit: false, t };
}

/**
 * Does the aim ray stop on `other`? (Owner, Oct 10: "seems like there's some autoaim going on, which is fine, but it
 * adjusts for my teammates.") The ray takes what the shot would: with friendly fire off a round passes a teammate
 * (decision 130 (d), core/bullets.ts), so the ray does too; with it on, a teammate in the way stops the ray — the shot
 * would hit them — and the caller warns. The dead and the shooter never stop it.
 */
export function aimRayStopsOn(shooter: Pick<PlayerState, 'id' | 'teamIndex'>, other: Pick<PlayerState, 'id' | 'teamIndex' | 'alive'>, config: { friendlyFire?: number } | undefined): boolean {
  if (!other.alive || other.id === shooter.id) return false;
  return other.teamIndex !== shooter.teamIndex || friendlyShare(config) > 0;
}
